import { cn } from "@/lib/utils";
import type { IconProps } from "@/types/general";
import { getComponentSize } from "@/types/general";
import React from "react";
import * as IconComponents from "@/icons/component";
import { isValidIconName } from "@/icons/icon-list";

interface FallbackIconProps extends React.SVGProps<SVGSVGElement> {
  title?: string;
}

// Neutral `lucide:circle-help` path. We intentionally inline the SVG
// (instead of importing from `@/icons/component`) to keep the fallback
// dependency-free — the fallback fires precisely when the icon registry is in
// a bad state, so it must not rely on it.
const HELP_OUTLINE_PATH =
  "M12.838 17.638q.362-.363.362-.888t-.362-.888-.888-.362-.887.363-.363.887.363.888.887.362.888-.363M11.05 14.15h1.85q0-.825.188-1.3t1.062-1.3q.65-.65 1.025-1.238T15.55 8.9q0-1.4-1.025-2.15T12.1 6q-1.425 0-2.312.75T8.55 8.55l1.65.65q.125-.45.563-.975T12.1 7.7q.8 0 1.2.438t.4.962q0 .5-.3.938t-.75.812q-1.1.975-1.35 1.475t-.25 1.825M12 22q-2.075 0-3.9-.787t-3.175-2.138T2.788 15.9 2 12t.788-3.9 2.137-3.175T8.1 2.788 12 2t3.9.788 3.175 2.137T21.213 8.1 22 12t-.788 3.9-2.137 3.175-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12t-2.325-5.675T12 4 6.325 6.325 4 12t2.325 5.675T12 20m0-8";

// Both fallbacks render the same neutral help-outline glyph. A missing or
// unresolved icon should NEVER look like an in-product alert — that misleads
// users into thinking something is wrong with their data.
const FallbackIcons = {
  Warning: ({ title = "Icon unavailable", ...props }: FallbackIconProps) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label={title}
      {...props}
    >
      <path d={HELP_OUTLINE_PATH} fill="currentColor" />
    </svg>
  ),

  Error: ({ title = "Icon unavailable", ...props }: FallbackIconProps) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label={title}
      {...props}
    >
      <path d={HELP_OUTLINE_PATH} fill="currentColor" />
    </svg>
  ),
};

const formatIconName = (name: string): string => {
  if (name.includes(':') === false) {
    return name;
  }
  
  const [prefix = '', iconName = ''] = name.split(':');
  
  const formatPart = (part: string): string => 
    part
      .split('-')
      .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join('');
  
  return formatPart(prefix) + formatPart(iconName);
};

const Icon = React.forwardRef<HTMLDivElement, IconProps>(
  ({ icon, className, size = 20, ...props }, ref) => {
    const pixelSize = getComponentSize(size);
    
    if (isValidIconName(icon) === false) {
      console.error(
        `Icon "${icon}" is not defined in the icon list. Add it to src/icons/icon-list.ts and run the icon generation script.`
      );
      
      return (
        <div
          ref={ref}
          className={cn("inline-flex items-center justify-center leading-none text-on-surface-variant/40", className)}
          {...props}
        >
          <FallbackIcons.Error
            width={pixelSize}
            height={pixelSize}
            title={`Icon "${icon}" not found`}
            className="block"
          />
        </div>
      );
    }

    const componentName = formatIconName(icon);
    const IconComponent = (IconComponents as unknown as { [key: string]: React.ComponentType<React.SVGProps<SVGSVGElement>> })[componentName];

    if (typeof IconComponent === 'undefined') {
      console.error(
        `Icon component "${componentName}" for "${icon}" not found. Please run 'npm run generate:icons' to generate icon components.`
      );

      return (
        <div
          ref={ref}
          className={cn("inline-flex items-center justify-center leading-none text-on-surface-variant/40", className)}
          {...props}
        >
          <FallbackIcons.Warning
            width={pixelSize}
            height={pixelSize}
            title={`Icon not found: ${icon}`}
            className="block"
          />
        </div>
      );
    }
    
    return (
      <div
        ref={ref}
        className={cn("inline-flex items-center justify-center leading-none text-[currentColor]", className)}
        {...props}
      >
        <IconComponent width={pixelSize} height={pixelSize} fill="currentColor" className="block" />
      </div>
    );
  }
);

Icon.displayName = "Icon";

export { Icon }; 